import { kmApart } from './map';

/** A point the phone recorded: where, when (ms since 1970), and how high, when it said. */
export interface Fix {
  lat: number;
  lon: number;
  at: number;
  alt: number | null;
}

/** How a stretch of a recording went, as its speeds say. */
export type Going = 'foot' | 'bike' | 'car' | 'train';

/** A stretch gone one way: its first and last point, how far, and how sure the guess is. */
export interface Stretch {
  going: Going;
  from: number;
  to: number;
  metres: number;
  start: number;
  end: number;
  /** 0–100: how many of its minutes agree. */
  sure: number;
}

/** Where the recording rested: ten minutes or more within 100 m. */
export interface Stay {
  lat: number;
  lon: number;
  start: number;
  end: number;
  minutes: number;
}

/** A point kept every 10 m, or 30 s when still. */
const EVERY_METRES = 10;
const EVERY_MS = 30_000;
/** A recording stops by itself after six hours. */
export const LONGEST_MS = 6 * 60 * 60 * 1000;
/** How near a stop is to have come to it, in metres. */
export const ARRIVED_METRES = 300;
/** A stay: so long within so far. */
const STAY_MS = 10 * 60 * 1000;
const STAY_METRES = 100;
/** The window speeds are read over. */
const WINDOW_MS = 60_000;

/** How far apart two fixes are, in metres. */
export function metresApart(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  return kmApart(a, b) * 1000;
}

/** Whether `next` is kept after `last`: 10 m on, or 30 s later. */
export function worthKeeping(last: Fix | undefined, next: Fix): boolean {
  return (
    last === undefined ||
    metresApart(last, next) >= EVERY_METRES ||
    next.at - last.at >= EVERY_MS
  );
}

/** A recording's length in metres and its climb, the GPS's wobble of 3 m left out. */
export function totals(fixes: readonly Fix[]): { metres: number; ascent: number } {
  let metres = 0;
  let ascent = 0;
  let level: number | null = null;
  fixes.forEach((fix, at) => {
    const before = fixes[at - 1];
    if (before) metres += metresApart(before, fix);
    if (fix.alt === null) return;
    if (level === null) level = fix.alt;
    else if (Math.abs(fix.alt - level) >= 3) {
      if (fix.alt > level) ascent += fix.alt - level;
      level = fix.alt;
    }
  });
  return { metres, ascent: Math.round(ascent) };
}

/** How a minute went, by its speed in km/h: a walk, a ride, a drive, a train. */
function goingAt(kmh: number): Going {
  if (kmh < 7) return 'foot';
  if (kmh < 25) return 'bike';
  if (kmh < 90) return 'car';
  return 'train';
}

/**
 * The stretches of a recording, each gone one way: its minutes read by their speed, the same
 * way joined, a stretch under 200 m or of a single minute given to the one before it.
 */
export function stretches(fixes: readonly Fix[]): Stretch[] {
  const first = fixes[0];
  if (first === undefined) return [];
  // Each window of a minute: its first and last point, and how it went.
  const windows: { from: number; to: number; going: Going }[] = [];
  let start = 0;
  for (let at = 1; at < fixes.length; at++) {
    const [a, b] = [fixes[start] as Fix, fixes[at] as Fix];
    if (b.at - a.at >= WINDOW_MS || at === fixes.length - 1) {
      let metres = 0;
      for (let step = start + 1; step <= at; step++) {
        metres += metresApart(fixes[step - 1] as Fix, fixes[step] as Fix);
      }
      const hours = Math.max(1, b.at - a.at) / 3_600_000;
      windows.push({ from: start, to: at, going: goingAt(metres / 1000 / hours) });
      start = at;
    }
  }
  const joined: (Stretch & { agree: number; of: number })[] = [];
  for (const window of windows) {
    const last = joined.at(-1);
    if (last?.going === window.going) {
      last.to = window.to;
      last.agree += 1;
      last.of += 1;
    } else {
      joined.push({ ...window, metres: 0, start: 0, end: 0, sure: 0, agree: 1, of: 1 });
    }
  }
  const made: Stretch[] = [];
  for (const one of joined) {
    let metres = 0;
    for (let step = one.from + 1; step <= one.to; step++) {
      metres += metresApart(fixes[step - 1] as Fix, fixes[step] as Fix);
    }
    const before = made.at(-1);
    // Under 200 m, or a single minute between two ways: the way before went on through it.
    if ((metres < 200 || one.of < 2) && before) {
      before.to = one.to;
      before.metres += metres;
      before.end = (fixes[one.to] as Fix).at;
      continue;
    }
    made.push({
      going: one.going,
      from: one.from,
      to: one.to,
      metres,
      start: (fixes[one.from] as Fix).at,
      end: (fixes[one.to] as Fix).at,
      sure: Math.round((one.agree / one.of) * 100),
    });
  }
  return made;
}

/** Where a recording rested: a run of points within 100 m of its first for ten minutes or more. */
export function stays(fixes: readonly Fix[]): Stay[] {
  const found: Stay[] = [];
  let at = 0;
  while (at < fixes.length) {
    const anchor = fixes[at] as Fix;
    let end = at;
    while (
      end + 1 < fixes.length &&
      metresApart(anchor, fixes[end + 1] as Fix) <= STAY_METRES
    ) {
      end += 1;
    }
    const last = fixes[end] as Fix;
    if (last.at - anchor.at >= STAY_MS) {
      found.push({
        lat: anchor.lat,
        lon: anchor.lon,
        start: anchor.at,
        end: last.at,
        minutes: Math.round((last.at - anchor.at) / 60_000),
      });
    }
    at = end + 1;
  }
  return found;
}

/** A recording as GPX, each point with its height and time: what a way's track is read from. */
export function gpxOf(fixes: readonly Fix[], name: string): string {
  const escaped = name.replace(/[<>&"]/g, (c) => `&#${String(c.charCodeAt(0))};`);
  const points = fixes
    .map(
      (fix) =>
        `<trkpt lat="${String(fix.lat)}" lon="${String(fix.lon)}">${
          fix.alt === null ? '' : `<ele>${String(fix.alt)}</ele>`
        }<time>${new Date(fix.at).toISOString()}</time></trkpt>`,
    )
    .join('');
  return `<?xml version="1.0" encoding="UTF-8"?><gpx version="1.1" creator="TripPath" xmlns="http://www.topografix.com/GPX/1/1"><trk><name>${escaped}</name><trkseg>${points}</trkseg></trk></gpx>`;
}

/** Where a recording is kept: on this phone only, by its trip. */
const key = (trip: string) => `trippath-recording-${trip}`;

/** A recording kept on this phone: its points, when it began, and the stop it is of. */
export interface Kept {
  stop: string;
  began: number;
  fixes: Fix[];
}

/** The recording this phone keeps of a trip; none when there is none or it cannot be read. */
export function keptRecording(trip: string): Kept | null {
  try {
    const said = localStorage.getItem(key(trip));
    return said === null ? null : (JSON.parse(said) as Kept);
  } catch {
    return null;
  }
}

/** Keeps a recording on this phone, or lets it go with `null`. */
export function keepRecording(trip: string, kept: Kept | null): void {
  try {
    if (kept === null) localStorage.removeItem(key(trip));
    else localStorage.setItem(key(trip), JSON.stringify(kept));
  } catch {
    // Not kept: the recording lives as long as the page.
  }
}
